import * as mutations from "../graphql/mutations";
import DisplayGeneratedText from "./DisplayGeneratedText";
import LinearProgressWithLabel from "./LinearProgressWIthLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiClient } from "../helpers/apiClient";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { CGI_BLACK, CGI_GREY, CGI_RED, CGI_WARNING } from "../helpers/enums";

function parsePayload(evalModuleData) {
  if (evalModuleData.twoDimPayload) return evalModuleData.twoDimPayload;

  try {
    const parsed = JSON.parse(evalModuleData.resultText || "{}");
    return parsed?.type === "TWO_DIMENSIONAL" ? parsed : {};
  } catch {
    return {};
  }
}

function ratingX(entry) {
  if (Number.isFinite(Number(entry.ratingX))) return Number(entry.ratingX);
  return (
    ((Number(entry.xAxis?.extent || 0) +
      Number(entry.xAxis?.scope || 0) +
      Number(entry.xAxis?.immutability || 0)) /
      3) *
    (Number(entry.xAxis?.probability || 0) / 100)
  );
}

function ratingY(entry) {
  if (Number.isFinite(Number(entry.ratingY))) return Number(entry.ratingY);
  return (
    Number(entry.yAxis?.extent || 0) *
    (Number(entry.yAxis?.probability || 0) / 100)
  );
}

export default function DisplayTwoDimModuleDataCurrent({
  evalModuleData,
  fetchEvaluation,
  setOpenLoading,
  setNotificationMessage,
  setOpenNotification,
  reloadPdf,
  index,
}) {
  const { t } = useTranslation();
  const payload = parsePayload(evalModuleData);
  const dataArray = payload.dataArray || [];
  const scatterData = dataArray.flatMap((question, questionIndex) =>
    (question.requirementAnswers || [])
      .filter((entry) => !entry.disabled)
      .map((entry, entryIndex) => ({
        name: `${t("subtopic")} ${index + 1}.${questionIndex + 1}.${entryIndex + 1}`,
        topName: question.title || question.content,
        content: entry.text || entry.impact,
        ratingX: ratingX(entry) + 1,
        ratingY: ratingY(entry) + 1,
        relevance: Number(entry.relevance || 0) + 1,
      })),
  );

  async function updateEvaluationModuleData(newText) {
    setOpenLoading(true);
    try {
      await apiClient.graphql({
        query: mutations.updateEvaluationModuleData,
        variables: {
          input: {
            id: evalModuleData.id,
            resultText: JSON.stringify({
              ...payload,
              generatedText: newText,
            }),
          },
        },
      });
      await fetchEvaluation();
      reloadPdf();
      setNotificationMessage(t("textSaveSuccessful"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenLoading(false);
    setOpenNotification(true);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mb: 4,
        pb: 4,
        borderBottom: 1,
        borderColor: CGI_BLACK,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          width: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Typography sx={{ fontSize: 19, fontWeight: 500, mr: 1 }}>
            {index + 1}.
          </Typography>
          <Typography sx={{ fontSize: 18, textDecoration: "underline", fontWeight: 500 }}>
            {evalModuleData.name}
          </Typography>
        </Box>
        {evalModuleData.completelyAnswered ? (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Typography>Impact</Typography>
            <LinearProgressWithLabel
              progressValue={(Number(payload.ratingX || 0) / 4) * 100}
              width={100}
            />
            <Typography>Financial</Typography>
            <LinearProgressWithLabel
              progressValue={(Number(payload.ratingY || 0) / 4) * 100}
              width={100}
            />
            <Typography>Relevance</Typography>
            <LinearProgressWithLabel
              progressValue={(Number(payload.relevance || 0) / 4) * 100}
              width={100}
            />
          </Box>
        ) : (
          <Typography sx={{ mb: 0.2, fontSize: 15, color: CGI_WARNING }}>
            {evalModuleData.name}: {t("notCompletelyAnswered")}
          </Typography>
        )}
      </Box>

      <Box sx={{ mx: 2.5, mt: 1, mb: 3, pt: 0.5, pb: 2, borderTop: 1, borderBottom: 1, borderColor: "#bbb" }}>
        <DisplayGeneratedText
          text={payload.generatedText || ""}
          saveAndRefetchText={updateEvaluationModuleData}
        />
      </Box>

      <Box sx={{ width: 1, minHeight: 500, mb: 3 }}>
        <ResponsiveContainer width="100%" height={500}>
          <ScatterChart margin={{ top: 30, right: 30, bottom: 10, left: 10 }}>
            <CartesianGrid />
            <XAxis type="number" dataKey="ratingX" domain={[1, 5]} name="Impact Materiality" />
            <YAxis type="number" dataKey="ratingY" domain={[1, 5]} name="Financial Materiality" />
            <ZAxis type="number" dataKey="relevance" range={[80, 1800]} name="Stakeholder relevance" />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              formatter={(value, name) => [Math.round(Number(value) * 100) / 100, name]}
            />
            <Legend />
            <Scatter name={evalModuleData.name} data={scatterData} fill={CGI_RED} />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>

      {dataArray.map((question, questionIndex) => (
        <Accordion
          key={question.questionId}
          sx={{
            border: 1,
            borderRadius: "20px !important",
            borderColor: CGI_GREY,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: 1 }}>
              <Typography sx={{ minWidth: 120, fontWeight: 500 }}>
                {t("subtopic")} {index + 1}.{questionIndex + 1}
              </Typography>
              <Typography sx={{ fontFamily: "Inter", fontSize: 13.5 }}>
                {question.title || question.content}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ borderTop: 1, borderColor: "#bbb" }}>
            <Typography sx={{ mb: 1, fontFamily: "Inter" }}>
              {question.content}
            </Typography>
            <Typography sx={{ mb: 2, fontStyle: "italic" }}>
              {t("comments")}: {question.answerText || "n/a"}
            </Typography>
            {(question.requirementAnswers || []).map((entry, entryIndex) => (
              <Box key={entryIndex} sx={{ borderTop: entryIndex ? 1 : 0, borderColor: "#ddd", py: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("impact")} {index + 1}.{questionIndex + 1}.{entryIndex + 1}: {entry.text || entry.impact}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mt: 0.5 }}>
                  <Typography>Impact: {ratingX(entry).toFixed(2)}</Typography>
                  <Typography>Financial: {ratingY(entry).toFixed(2)}</Typography>
                  <Typography>Relevance: {Number(entry.relevance || 0)}</Typography>
                  {entry.disabled && <Typography>{t("notRelevant")}</Typography>}
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography>{t("risk")}: {entry.risk || "n/a"}</Typography>
                <Typography>{t("opportunity")}: {entry.opportunity || "n/a"}</Typography>
                <Typography>{t("example")}: {entry.example || "n/a"}</Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
